import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';
import {SemVer} from 'semver';

import FieldError from 'ui-library/FieldError';
import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';

import {biggestSoFarWithSameMajor, isNewerVersionThanOthers} from './BenchlingAppVersionInput';

const yaml = require('js-yaml');

interface Props {
  currentVersions: Array<string>;
  uploadedFile: File | null;
  onChangeFile: (file: File | null, fileVersion: string | null) => void;
}

export interface VersionOrErrorMessage {
  version?: string;
  error?: string;
}

function isFilenameInvalid(file: File): boolean {
  if (file == null) {
    return true;
  }
  const maybeFileType = file.name.split('.').pop();
  return !(maybeFileType === 'yaml' || maybeFileType === 'yml');
}

export default function ManifestUpload(props: Props): JSX.Element {
  const [fileError, setFileError] = useState<string | null>(null);
  const {uploadedFile, currentVersions} = props;

  async function getVersionFromFile(file: File): Promise<string | null> {
    try {
      const manifest = yaml.load(await file.text());
      const newValue = new SemVer(manifest.info.version);
      const previous = currentVersions.map((v) => new SemVer(v));
      const isVersionOk = isNewerVersionThanOthers(newValue, previous);

      if (isVersionOk) {
        setFileError(null);
        return newValue.format();
      }
      const biggestSoFar = biggestSoFarWithSameMajor(newValue.major, previous);
      setFileError(
        `Version ${manifest.info.version} in file "${file.name}" must be greater than ${biggestSoFar}`
      );
      return null;
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch {
      setFileError(`Can't locate a version in file "${file.name}"`);
      return null;
    }
  }

  async function onChangeFile(file: File | null): Promise<void> {
    if (file == null) {
      setFileError(null);
      props.onChangeFile(file, null);
    } else if (isFilenameInvalid(file)) {
      setFileError('Uploaded file must have suffix .yaml or .yml');
      props.onChangeFile(file, null);
    } else {
      const version = await getVersionFromFile(file);
      props.onChangeFile(file, version);
    }
  }

  useEffect(() => {
    onChangeFile(uploadedFile);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  return (
    <>
      {!uploadedFile ? (
        <FileChooser
          className={css(styles.formInput, styles.fileChooser)}
          onSelectFiles={(...files: Array<File>) => {
            if (files.length === 1) {
              onChangeFile(files[0]);
            }
          }}
          accept=".yaml,.yml"
        />
      ) : (
        <FileList>
          <FileRow
            file={uploadedFile}
            onRequestRemove={() => {
              onChangeFile(null);
            }}
          />
        </FileList>
      )}
      {fileError && <FieldError message={fileError} />}
    </>
  );
}

const styles = StyleSheet.create({
  formInput: {
    marginBottom: 15,
  },
  fileChooser: {
    height: '12em',
    paddingTop: '4em',
  },
});
